type DeviceVisibilityApiInput = {
  showOnAndroid?: boolean
  showOnAndroid2?: boolean
  showOnIos?: boolean
  showOnIOS?: boolean
  showOnWeb?: boolean
  showOnWP?: boolean
}

type LeafletDeviceCode = 'IOS' | 'ANDROID' | 'MPROMOCJE' | 'WEB' | 'WP'

type DeviceVisibilityHardIosApi = {
  showOnAndroid: boolean
  showOnAndroid2: boolean
  showOnIOS: boolean
  showOnWeb: boolean
}

type DeviceVisibilityHardIosWithWpApi = DeviceVisibilityHardIosApi & {
  showOnWP: boolean
}

type DeviceVisibilityMinimalApi = {
  showOnAndroid: boolean
  showOnIos: boolean
  showOnWeb: boolean
}

export type DeviceVisibilityModel = {
  isVisibleOnAndroid: boolean
  isVisibleOnAndroid2: boolean
  isVisibleOnIos: boolean
  isVisibleOnWeb: boolean
  isVisibleOnWp: boolean
}

export type DeviceVisibilityMinimalModel = {
  isVisibleOnAndroid: boolean
  isVisibleOnIos: boolean
  isVisibleOnWeb: boolean
}

export type DeviceVisibilityWithoutWpModel = Omit<DeviceVisibilityModel, 'isVisibleOnWp'>

type MinimalOnlyVisibilityModel = DeviceVisibilityMinimalModel &
  Partial<Record<'isVisibleOnAndroid2' | 'isVisibleOnWp', never>>

type WithoutWpOnlyVisibilityModel = DeviceVisibilityWithoutWpModel & Partial<Record<'isVisibleOnWp', never>>

function mergeWithRest<TRest extends object, TApi extends object>(rest: TRest, apiFields: TApi): TRest & TApi {
  return {
    ...rest,
    ...apiFields
  }
}

function splitFullDeviceVisibility<T extends DeviceVisibilityModel>(model: T) {
  const { isVisibleOnAndroid, isVisibleOnAndroid2, isVisibleOnIos, isVisibleOnWeb, isVisibleOnWp, ...rest } = model

  return {
    rest,
    visibility: {
      isVisibleOnAndroid,
      isVisibleOnAndroid2,
      isVisibleOnIos,
      isVisibleOnWeb,
      isVisibleOnWp
    }
  }
}

function splitWithoutWpDeviceVisibility<T extends WithoutWpOnlyVisibilityModel>(model: T) {
  const { isVisibleOnAndroid, isVisibleOnAndroid2, isVisibleOnIos, isVisibleOnWeb, isVisibleOnWp, ...rest } = model
  void isVisibleOnWp

  return {
    rest,
    visibility: {
      isVisibleOnAndroid,
      isVisibleOnAndroid2,
      isVisibleOnIos,
      isVisibleOnWeb
    }
  }
}

function splitMinimalDeviceVisibility<T extends MinimalOnlyVisibilityModel>(model: T) {
  const { isVisibleOnAndroid, isVisibleOnIos, isVisibleOnWeb, isVisibleOnAndroid2, isVisibleOnWp, ...rest } = model
  void isVisibleOnAndroid2
  void isVisibleOnWp

  return {
    rest,
    visibility: {
      isVisibleOnAndroid,
      isVisibleOnIos,
      isVisibleOnWeb
    }
  }
}

function normalizeDeviceVisibility(api: DeviceVisibilityApiInput) {
  return {
    isVisibleOnAndroid: api.showOnAndroid ?? false,
    isVisibleOnAndroid2: api.showOnAndroid2 ?? false,
    isVisibleOnIos: api.showOnIOS ?? api.showOnIos ?? false,
    isVisibleOnWeb: api.showOnWeb ?? false,
    isVisibleOnWp: api.showOnWP ?? false
  }
}

export function deviceVisibilityFromLeafletDevices(
  devices: readonly LeafletDeviceCode[] | readonly string[] = []
): DeviceVisibilityModel {
  return {
    isVisibleOnIos: devices.includes('IOS'),
    isVisibleOnAndroid: devices.includes('ANDROID'),
    isVisibleOnAndroid2: devices.includes('MPROMOCJE'),
    isVisibleOnWeb: devices.includes('WEB'),
    isVisibleOnWp: devices.includes('WP')
  }
}

function createFullSerializer<TApi extends object>(buildApiFields: (visibility: DeviceVisibilityModel) => TApi) {
  return function <T extends DeviceVisibilityModel>(model: T): Omit<T, keyof DeviceVisibilityModel> & TApi {
    const { rest, visibility } = splitFullDeviceVisibility(model)

    return mergeWithRest(rest, buildApiFields(visibility))
  }
}

function createWithoutWpSerializer<TApi extends object>(
  buildApiFields: (visibility: DeviceVisibilityWithoutWpModel) => TApi
) {
  return function <T extends WithoutWpOnlyVisibilityModel>(model: T): Omit<T, keyof DeviceVisibilityModel> & TApi {
    const { rest, visibility } = splitWithoutWpDeviceVisibility(model)

    return mergeWithRest(rest, buildApiFields(visibility))
  }
}

function createMinimalSerializer<TApi extends object>(
  buildApiFields: (visibility: DeviceVisibilityMinimalModel) => TApi
) {
  return function <T extends MinimalOnlyVisibilityModel>(model: T): Omit<T, keyof DeviceVisibilityModel> & TApi {
    const { rest, visibility } = splitMinimalDeviceVisibility(model)

    return mergeWithRest(rest, buildApiFields(visibility))
  }
}

const DEVICE_VISIBILITY_PRESETS = {
  BASE_WITHOUT_WP: (visibility: DeviceVisibilityWithoutWpModel): DeviceVisibilityHardIosApi => ({
    showOnAndroid: visibility.isVisibleOnAndroid,
    showOnAndroid2: visibility.isVisibleOnAndroid2,
    showOnIOS: visibility.isVisibleOnIos,
    showOnWeb: visibility.isVisibleOnWeb
  }),
  BASE_WITH_WP: (visibility: DeviceVisibilityModel): DeviceVisibilityHardIosWithWpApi => ({
    showOnAndroid: visibility.isVisibleOnAndroid,
    showOnAndroid2: visibility.isVisibleOnAndroid2,
    showOnIOS: visibility.isVisibleOnIos,
    showOnWeb: visibility.isVisibleOnWeb,
    showOnWP: visibility.isVisibleOnWp
  }),
  MINIMAL: (visibility: DeviceVisibilityMinimalModel): DeviceVisibilityMinimalApi => ({
    showOnAndroid: visibility.isVisibleOnAndroid,
    showOnIos: visibility.isVisibleOnIos,
    showOnWeb: visibility.isVisibleOnWeb
  })
}

export function deviceVisibilityFromApi<T extends DeviceVisibilityApiInput>(
  api: T
): Omit<T, keyof DeviceVisibilityApiInput> & DeviceVisibilityModel {
  const { showOnAndroid, showOnAndroid2, showOnIos, showOnIOS, showOnWeb, showOnWP, ...rest } = api

  return {
    ...rest,
    ...normalizeDeviceVisibility({ showOnAndroid, showOnAndroid2, showOnIos, showOnIOS, showOnWeb, showOnWP })
  }
}

export function deviceVisibilityMinimalFromApi<T extends DeviceVisibilityApiInput>(
  api: T
): Omit<T, keyof DeviceVisibilityApiInput> & DeviceVisibilityMinimalModel {
  const { showOnAndroid, showOnAndroid2, showOnIos, showOnIOS, showOnWeb, showOnWP, ...rest } = api
  const normalized = normalizeDeviceVisibility({
    showOnAndroid,
    showOnAndroid2,
    showOnIos,
    showOnIOS,
    showOnWeb,
    showOnWP
  })

  return {
    ...rest,
    isVisibleOnAndroid: normalized.isVisibleOnAndroid,
    isVisibleOnIos: normalized.isVisibleOnIos,
    isVisibleOnWeb: normalized.isVisibleOnWeb
  }
}

export const deviceVisibilityToApiWithWp = createFullSerializer(DEVICE_VISIBILITY_PRESETS.BASE_WITH_WP)

export const deviceVisibilityToMinimalApi = createMinimalSerializer(DEVICE_VISIBILITY_PRESETS.MINIMAL)

export const deviceVisibilityToApiWithoutWp = createWithoutWpSerializer(DEVICE_VISIBILITY_PRESETS.BASE_WITHOUT_WP)
